import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { EquipmentModel } from '../data/types';
import DatabaseAssetsMethods from '../data/database.assets.methods';
import { inspectionDB } from '../data/database.structure';
import { queryKeys } from '../store/queryKeys';
import { EMutationKey } from '../store/mutationKeys';

interface CurrentInspectionAssetEquipmentState {
  bucket: EquipmentModel | null;
  yardBuckets: EquipmentModel[];
  isChangeout: boolean;
  isLoading: boolean;
  error: string | null;
}

interface CurrentInspectionAssetEquipmentProps {
  inspectionId: string;
}

export const useCurrentInspectionAssetEquipmentCached = ({
  inspectionId,
}: CurrentInspectionAssetEquipmentProps) => {
  const queryClient = useQueryClient();

  const {
    data: result,
    error,
    isLoading,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: queryKeys.inspectionAssetEquipment(inspectionId),
    queryFn: async () => {
      if (!inspectionId) {
        throw new Error('Inspection ID is required');
      }

      console.log(
        '🔍 Fetching inspection asset equipment from database:',
        inspectionId,
      );
      const assetMethods = new DatabaseAssetsMethods(inspectionDB);

      // Get Inspection Asset Equipment
      const equipment = await assetMethods.getInspectionAssetCurrentEquipment({
        inspectionId: inspectionId,
      });

      console.log(
        '✅ Retrieved inspection asset equipment:',
        inspectionId,
        'bucket:',
        equipment.bucket?.name || 'none',
        'yard buckets:',
        equipment.unassignedEquipment.length,
      );

      return {
        bucket: equipment.bucket ?? null,
        yardBuckets: equipment.unassignedEquipment || [],
        isChangeout: equipment.isChanged || false,
      };
    },
    enabled: !!inspectionId,
    // Cache equipment data for reasonable time since it doesn't change frequently during an inspection
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    // Enable background refetch to keep data fresh
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    // Retry on failure
    retry: 2,
    networkMode: 'always',
  });

  // Listen for mutations that might affect equipment assignments
  useEffect(() => {
    if (!inspectionId) return;

    const handleMutationSuccess = () => {
      // Small delay to ensure database changes are committed
      setTimeout(() => {
        console.log(
          '🔄 Refetching inspection asset equipment after mutation:',
          inspectionId,
        );
        refetch();
      }, 100);
    };

    const mutationObserver = queryClient.getMutationCache();

    const unsubscribe = mutationObserver.subscribe((mutation) => {
      if (mutation.mutation?.state.status === 'success') {
        const mutationKey = mutation.mutation.options.mutationKey?.[0];

        // Refresh if any observation mutation or equipment-related changes occur
        const shouldRefresh = [
          EMutationKey.upsertObservation,
          EMutationKey.deleteObservationByPath,
          EMutationKey.deleteObservationsByProps,
          EMutationKey.deleteObservation,
          EMutationKey.syncInspectionData,
          // Equipment changes might also affect this data
          'equipmentAssignment',
          'bucketChangeout',
        ].includes(mutationKey as EMutationKey);

        if (shouldRefresh) {
          handleMutationSuccess();
        }
      }
    });

    return unsubscribe;
  }, [inspectionId, queryClient, refetch]);

  const state: CurrentInspectionAssetEquipmentState = {
    bucket: result?.bucket || null,
    yardBuckets: result?.yardBuckets || [],
    isChangeout: result?.isChangeout || false,
    isLoading,
    error: error?.message || null,
  };

  return {
    ...state,
    refetch,
    isFetching,
    // Indicate if data might be stale
    isStale:
      queryClient.getQueryState(
        queryKeys.inspectionAssetEquipment(inspectionId),
      )?.isInvalidated || false,
  };
};
